import { useEffect, useState } from 'react';
import { VideoJsPlayer } from 'video.js';
import {  Caption, settingControllerKey } from '../../PlayerModels';
import { CustomOption, CustomOptionWrapper, SelectingItemMainWrapper, SettingPanelMultiSelectItem } from '../../styles';
import { PanelSettingHeader, PanelSettingLayer } from '../../styles/Panel';
import { __t } from '../../../../i18n';
import { IconCaption } from '../../../../icons';
import { Box } from '../../../../styles';
import UsePlayerProps from '../../../../Store/usePlayerProps';
import { findCaptionLabel } from '../../PlayerServices';

const ControlBarCaptionPanel = ({ player, showCaptions, changePosition, handleSettingItemClick, activeSetting }:
  {
    player: VideoJsPlayer, showCaptions: boolean, changePosition: boolean,
    handleSettingItemClick: (controllerKey: settingControllerKey) => void
    activeSetting: string | null
  }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [captions, setCaptions] = useState<Caption[]>([]);
  // const { tech } = useTech();
  const trackWrapper = document.querySelector('.vjs-text-track-display');
  const setPlayerState = UsePlayerProps((state) => state.setPlayerState);
  const playerState = UsePlayerProps((state) => state.playerState);
  const selectedCaptionLang = UsePlayerProps((state) => state.playerState.captionState.selectedCaptionLang);
  const defaultSub = UsePlayerProps((state) => state.playerState.captionState.prevCaptionIndex);

  useEffect(() => {
    player.textTracks().addEventListener('change', handleUpdate);

    return () => {
      player.textTracks().removeEventListener('change', handleUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAvailableCaptions();
    // Every source change (an ad in, the content back) empties the track list and refills it,
    // on iPhone asynchronously, so the list read on mount can miss the subtitles.
    const tracks = player.textTracks();
    tracks.addEventListener('addtrack', updateCaptionList);
    tracks.addEventListener('removetrack', updateCaptionList);

    return () => {
      tracks.removeEventListener('addtrack', updateCaptionList);
      tracks.removeEventListener('removetrack', updateCaptionList);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, showCaptions]);

  useEffect(() => {
    changePosition ? trackWrapper?.classList.add('subPosition') : trackWrapper?.classList.remove('subPosition');
    const videoElement = document.getElementById('custom-player-wrapper');
    if (selectedCaptionLang.lang === 'fa') {
      videoElement?.classList.add('subRtlDirection');
      videoElement?.classList.remove('subLtrDirection');
    } else {
      videoElement?.classList.add('subLtrDirection');
      videoElement?.classList.remove('subRtlDirection');
    }
    player.textTrackSettings.updateDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePosition, selectedCaptionLang]);

  // Bound once on mount, so it reads the store itself: this render's playerState would be stale
  // by the time a track changes, and writing it back would undo later changes to the store.
  const handleUpdate = () => {
    const current = UsePlayerProps.getState().playerState;
    const tracks = player.textTracks().tracks_;
    const selected = tracks.find((t) => t.mode === 'showing');
    if (selected && (selected.language !== current.captionState.selectedCaptionLang.lang)) {
      const index = tracks.findIndex((elem) => elem.language === selected.language);
      setPlayerState({
        ...current,
        captionState: {
          prevCaptionIndex: selected.language === 'off' ? -1 : index,
          selectedCaptionLang: {
            lang: selected.language,
            label: findCaptionLabel(selected.language),
          },
        },
      });
    }
  };

  const updateCaptionList = () => {
    const captions: any = [
      {
        language: 'off',
        label: __t('player.off'),
        mode: 'disabled',
      },
    ];
    player.textTracks().tracks_.forEach((track) => {
      if (track.kind === 'subtitles') {
        captions.push({
          language: track.language,
          label: findCaptionLabel(track.language),
        });
      }
    });
    setCaptions(captions);
  };

  const setAvailableCaptions = () => {
    player.textTracks().tracks_.forEach((track, index) => {
      if (track.kind === 'subtitles') {
        track.default = defaultSub !== -1 && track.language === 'fa';
        track.mode = defaultSub !== -1 && track.language === 'fa' ? 'showing' : 'disabled';
        if (defaultSub !== -1 && track.language === 'fa') {
          setPlayerState({
            ...playerState,
            captionState: {
              prevCaptionIndex: index,
              selectedCaptionLang: {
                lang: track.language,
                label: findCaptionLabel(track.language),
              },
            },
          });
        }
      }
    });
    updateCaptionList();
  };

  const toggleMenu = (e, key) => {
    e.stopPropagation();
    handleSettingItemClick(key);
    setIsOpen(!isOpen);
  };

  const handleCaptionChange = (lang: string, label:string) => {
    const _player = player.textTracks().tracks_;
    const index = _player.findIndex((elem) => elem.language === lang);
    setPlayerState({
      ...playerState,
      captionState: {
        prevCaptionIndex: lang === 'off' ? -1 : index,
        selectedCaptionLang: {
          lang,
          label,
        },
      },
    });
    _player.forEach((elem, i) => {
      elem.mode = 'disabled';
      if (i === index) {
        elem.mode = 'showing';
      }
    });
  };

  return (
    <>
      {!activeSetting && <SettingPanelMultiSelectItem icon={<IconCaption />} label="player.caption" onClick={(e) => toggleMenu(e, 'caption')} value={selectedCaptionLang.label as string} />}
      {activeSetting === 'caption' && <PanelSettingLayer>
        <CaptionList
          returnAction={(e) => toggleMenu(e, null)}
          selectedValue={selectedCaptionLang.lang}
          list={captions}
          onChange={(lang: string, label: string) => {
            if (lang !== selectedCaptionLang.lang) {
              handleCaptionChange(lang, label);
            }
            setIsOpen(false);
          }} />
      </PanelSettingLayer>}
    </>
  );
};

export default ControlBarCaptionPanel;
const CaptionList = ({ selectedValue, list, onChange, returnAction }) => {
  return (
    <Box>
      <PanelSettingHeader label={'player.caption'} handleClick={(e) => returnAction(e)} />
      <SelectingItemMainWrapper>
        {list.map(({ language, label }) => (<CustomOptionWrapper key={language} onClick={(e) => {
          onChange(language, label);
          returnAction(e);
        }}>
          <CustomOption selected={language === selectedValue}>
            {label}
          </CustomOption>
        </CustomOptionWrapper>))}
      </SelectingItemMainWrapper>
    </Box>
  )
};
